import "./AdminUser.css";
import NavBar from "../../component/navBar/NavBar";
import Title from "../../component/title/Title";
import Table from "../../component/table/Table";
import { useCallback, useEffect, useRef, useState } from "react";
import useMasterAdminFormHeader from "../masterAdmin/useMasterAdminFormHeader";
import TextInput from "../../component/textInput/TextInput";
import Dropdown from "../../component/dropdown/Dropdown";
import DeleteModal from "../../component/modal/DeleteModal";
import InviteUsersModal from "../../component/modal/InviteUsersModal";
import Checkbox from "../../component/checkbox/Checkbox";
import {
  PrimaryButton,
  SecondaryButton,
} from "../../component/buttonComponent/ButtonComponent";
import SnackBar from "../../component/Snackbar/Snackbar";
import CustomPhoneInput from "../../component/customPhoneInput/CustomPhoneInput";
import { isValidPhoneNumber } from "react-phone-number-input";
import {
  addPlusPhone,
  removePlusPhone,
} from "../../config/utilities/formatPhone";
import { useAuth } from "../../config/utilities/useAuth";
import {
  APIDeleteAdministrators,
  APIGetAdministrators,
  APIUpdateAdministrators,
} from "../../config/utilities/APIs";

const headers = [
  { key: "fullName", name: "Full Name" },
  { key: "email", name: "Email" },
  { key: "role", name: "Role" },
];

export default function AdminUser({
  institutionIdOverride,
  masterAdminMode = false,
}) {
  const privilegesWithCheckboxes = [
    {
      name: "Live Module",
      checkboxes: [
        { key: "mapOverview", label: "Map Overview" },
        { key: "dispatchBoard", label: "Dispatch Board" },
      ],
    },
    {
      name: "Fleet Management Module",
      checkboxes: [
        { key: "vehicles", label: "Vehicles" },
        { key: "drivers", label: "Drivers" },
      ],
    },
    {
      name: "Manage Users Module",
      checkboxes: [
        { key: "administrators", label: "Administrators" },
        { key: "familyManagement", label: "Family Management" },
      ],
    },
    {
      name: "Schedule Management Module",
      checkboxes: [{ key: "routeManagement", label: "Route Management" }],
    },
    {
      name: "Notifications Campaign Module",
      checkboxes: [{ key: "campaignManagement", label: "Campaign Management" }],
    },
    {
      name: "Scheduling Module",
      checkboxes: [{ key: "scheduling", label: "Scheduling" }],
    },
  ];

  const [action, setAction] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [selectedRowData, setSelectedRowData] = useState(null);
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [snackbarType, setSnackbarType] = useState("");
  const [snackbarTitle, setSnackbarTitle] = useState("");
  const [snackbarSubtitle, setSnackbarSubtitle] = useState("");
  const [loading, setLoading] = useState(false);
  const [isSubmitDisabled, setIsSubmitDisabled] = useState(true);
  const [isMobileViewport, setIsMobileViewport] = useState(
    typeof window !== "undefined" ? window.innerWidth <= 768 : false,
  );
  const [keyUp, setKeyUp] = useState("", false);
  const [fetchedAdministratorData, setFetchedAdministratorData] = useState([]);
  const [initialEditInput, setInitialEditInput] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");

  const { privilege, token } = useAuth();
  const role = privilege?.role;
  const institutionId = institutionIdOverride ?? privilege?.institutionId;
  const nameInputWidth = isMobileViewport ? "100%" : "20rem";
  const fullInputWidth = isMobileViewport ? "100%" : "46.9rem";
  const emailInputWidth = isMobileViewport ? "100%" : "45rem";

  useEffect(() => {
    const handleResize = () => setIsMobileViewport(window.innerWidth <= 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const accessPrivilegeOptions = [
    { label: "Viewer", value: "VIEWER" },
    { label: "Editor", value: "EDITOR" },
    { label: "Admin", value: "ADMIN" },
    ...(role === "SUPERADMIN"
      ? [{ label: "Superadmin", value: "SUPERADMIN" }]
      : []),
  ];

  const fetchAdministrators = async (page = 1, searchTerm) => {
    setLoading(true);
    try {
      const response = await APIGetAdministrators(
        setLoading,
        page,
        searchTerm,
        institutionId,
      );

      if (response.status === 200) {
        const { data } = response.data;
        setFetchedAdministratorData(data);
        setPageNumber(page);
      } else if (response.status === 400) {
        console.warn("Bad Request:", response);
      } else if (response.status === 401) {
        console.warn("Unauthorized:", response);
      } else if (response.status === 403) {
        console.warn("Forbidden:", response);
      } else if (response.status === 404) {
        console.warn("Not Found:", response);
      } else if (response.status >= 500) {
        console.error("Server Error:", response);
      } else {
        console.warn("Unexpected Status Code:", response.status, response);
      }
    } catch (error) {
      console.error("Error fetching admins:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!institutionId) return;

    fetchAdministrators();

    const handleCookieUpdate = () => {
      if (!masterAdminMode) {
        fetchAdministrators();
      }
    };

    window.addEventListener("cookieUpdate", handleCookieUpdate);

    return () => {
      window.removeEventListener("cookieUpdate", handleCookieUpdate);
    };
  }, [institutionId, masterAdminMode]);

  useEffect(() => {
    if (!institutionId) return;
    fetchAdministrators(pageNumber, searchTerm);
  }, [pageNumber, searchTerm, institutionId]);

  const [input, setInput] = useState({
    firstName: "",
    lastName: "",
    email: "",
    role: "",
    phoneNumber: "",
    privileges: [],
  });

  const [error, setError] = useState({
    firstName: false,
    lastName: false,
    email: false,
    phoneNumber: false,
    privileges: false,
    unchanged: false,
  });

  const handleResetRef = useRef(async () => {});

  const getAdminFormViewConfig = useCallback((currentAction) => {
    switch (currentAction) {
      case "edit":
        return {
          title: "Edit Administrator",
          subtitle: "Update administrator details for this workspace",
          backLabel: "Back to Administrators",
        };
      case "view":
        return {
          title: "View Administrator",
          subtitle: "Administrator details for this workspace",
          backLabel: "Back to Administrators",
        };
      default:
        return null;
    }
  }, []);

  const clearFormHeader = useMasterAdminFormHeader({
    enabled: masterAdminMode,
    action: action === "edit" || action === "view" ? action : null,
    getViewConfig: getAdminFormViewConfig,
    onBack: () => handleResetRef.current(),
  });

  const handleReset = async () => {
    if (masterAdminMode) {
      clearFormHeader();
    }

    setInput({
      firstName: "",
      lastName: "",
      email: "",
      role: "",
      phoneNumber: "",
      privileges: [],
    });

    setError({
      firstName: false,
      lastName: false,
      email: false,
      phoneNumber: false,
      privileges: false,
      unchanged: false,
    });

    setAction(null);
    setSelectedRowData(null);
    setInitialEditInput(null);
    setLoading(false);
    setShowInviteModal(false);
    setPageNumber(1);
    await fetchAdministrators(1, searchTerm);
  };

  handleResetRef.current = handleReset;

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    const updatedInput = { ...input, [name]: value };

    setInput(updatedInput);
  };

  const handlePopoverAction = (action, rowId) => {
    if (action === "edit") {
      handleOpenEdit(rowId);
    } else if (action === "delete") {
      setShowDeleteModal(true);
      const selectedRow = fetchedAdministratorData.admins.find(
        (row) => row._id === rowId,
      );
      setSelectedRowData(selectedRow);
    } else {
      handleOpenView(rowId);
    }
  };

  const handleOpenView = (rowId) => {
    const selectedRow = fetchedAdministratorData.admins.find(
      (row) => row._id === rowId,
    );

    if (selectedRow) {
      // Convert "TRUE" and "FALSE" strings to actual boolean values
      const privilegesWithBooleans = Object.fromEntries(
        Object.entries(selectedRow.privileges).map(([key, value]) => [
          key,
          value === "TRUE",
        ]),
      );

      const formattedPhone = addPlusPhone(selectedRow.phoneNumber);

      const newInput = {
        _id: selectedRow._id || "",
        email: selectedRow.email || "",
        firstName: selectedRow.firstName || "",
        lastName: selectedRow.lastName || "",
        role: selectedRow.role || "",
        phoneNumber: formattedPhone || "",
        privileges: privilegesWithBooleans || {},
      };
      setInput(newInput);
    }
    setAction("view");
  };

  const handleOpenEdit = (rowId) => {
    const selectedRow = fetchedAdministratorData.admins.find(
      (row) => row._id === rowId,
    );

    if (selectedRow) {
      // Convert "TRUE" and "FALSE" strings to actual boolean values
      const privilegesWithBooleans = Object.fromEntries(
        Object.entries(selectedRow.privileges).map(([key, value]) => [
          key,
          value === "TRUE",
        ]),
      );

      const formattedPhone = addPlusPhone(selectedRow.phoneNumber);

      const newInput = {
        _id: selectedRow._id || "",
        email: selectedRow.email || "",
        firstName: selectedRow.firstName || "",
        lastName: selectedRow.lastName || "",
        role: selectedRow.role || "",
        phoneNumber: formattedPhone || "",
        privileges: privilegesWithBooleans || {},
      };

      setInput(newInput);
      setInitialEditInput(newInput);

      setSelectedRowData(selectedRow);
    }
    setAction("edit");
  };

  const handleOpenModalInvite = () => {
    setShowInviteModal(true);
  };

  const handleEdit = async () => {
    setLoading(true);

    const {
      _id,
      email,
      privileges,
      role,
      phoneNumber,
      firstName,
      lastName,
      ...unformattedAdminData
    } = input;

    let updatedPrivileges = { ...privileges };

    if (role === "ADMIN") {
      updatedPrivileges = Object.fromEntries(
        Object.keys(privileges).map((key) => [key, true]),
      );
    }

    const formattedPrivileges = Object.fromEntries(
      Object.entries(updatedPrivileges).map(([key, value]) => [
        key,
        value ? "TRUE" : "FALSE",
      ]),
    );

    const formattedPhone = removePlusPhone(phoneNumber);

    const adminData = {
      ...unformattedAdminData,
      role,
      phoneNumber: formattedPhone,
      privileges: formattedPrivileges,
      firstName,
      lastName,
    };

    try {
      const response = await APIUpdateAdministrators(
        _id,
        setLoading,
        adminData,
        institutionId,
        token,
      );

      if (response?.status === 200) {
        setShowSnackbar(true);
        setSnackbarTitle("Update Successful");
        setSnackbarSubtitle("Admin user has been updated successfully");
        setSnackbarType("success");
      } else {
        throw new Error(
          response?.response?.data?.message || "Failed to update administrator",
        );
      }
    } catch (error) {
      setShowSnackbar(true);
      setSnackbarTitle("Update Failed");
      setSnackbarSubtitle(error.message || "An error occurred");
      setSnackbarType("error");
    } finally {
      await fetchAdministrators();
      handleReset();
      setLoading(false);
    }
  };

  const handleDelete = async (row) => {
    setLoading(true);

    const rowId = row._id;

    try {
      const response = await APIDeleteAdministrators(
        rowId,
        setLoading,
        institutionId,
        token,
      );

      if (response?.status === 200) {
        setShowSnackbar(true);
        setSnackbarTitle("Delete Successful");
        setSnackbarSubtitle("Administrator has been deleted successfuly");
        setSnackbarType("success");
      } else {
        throw new Error(
          response?.response?.data?.message || "Failed to delete administrator",
        );
      }
    } catch (error) {
      setShowSnackbar(true);
      setSnackbarTitle("Delete Failed");
      setSnackbarSubtitle(error.message || "An error occurred");
      setSnackbarType("error");
    } finally {
      await fetchAdministrators();
      setShowDeleteModal(false);
      setLoading(false);
      handleReset();
    }
  };

  const handleCheckboxChange = (checkboxKey) => {
    const updatedPrivileges = {
      ...input.privileges,
      [checkboxKey]: !input.privileges[checkboxKey],
    };

    const updatedInput = { ...input, privileges: updatedPrivileges };
    setInput(updatedInput);
  };

  const validateInputs = (input) => {
    let isValid = true;

    // Regular expressions
    const nameRegex = /^[A-Za-z\s]+$/;
    const emailRegex = /^[^\s@]+@[^\s@]+$/;

    // Validate fields
    const newErrors = {
      firstName: input.firstName.trim() && !nameRegex.test(input.firstName),
      lastName: input.lastName.trim() && !nameRegex.test(input.lastName),
      email: input.email.trim() && !emailRegex.test(input.email),
      role: !input.role.trim(),
      phoneNumber: input.phoneNumber
        ? !isValidPhoneNumber(input.phoneNumber)
        : true, // Ensure it's a valid string before validation
      privileges: Object.values(input.privileges).every(
        (value) => value === false,
      ),
      unchanged: JSON.stringify(input) === JSON.stringify(initialEditInput),
    };

    isValid = !Object.values(newErrors).includes(true);
    setError(newErrors);
    setIsSubmitDisabled(!isValid);

    return isValid;
  };

  // Re-run validation whenever input changes
  useEffect(() => {
    validateInputs(input);
  }, [input]);

  const renderTitle = () => {
    switch (action) {
      case "edit":
        return (
          <Title
            title="Edit Administrator"
            titleSize="1.5rem"
            gap="0.5rem"
            lineHeight="2.5rem"
          />
        );
      case "view":
        return (
          <Title
            title="View Administrator"
            titleSize="1.5rem"
            gap="0.5rem"
            lineHeight="2.5rem"
          />
        );
      default:
        return (
          <Title
            title="Administrators"
            subtitle="Manage bus buddy administrators"
            titleSize="1.5rem"
            subtitleSize="1rem"
            gap="0.5rem"
            lineHeight="2rem"
          />
        );
    }
  };

  const renderContent = () => {
    switch (action) {
      case "edit":
        return (
          <>
            <div className="name-form-container">
              <div className="input-label">Name</div>
              <div className="input-containers">
                <div className="input-container">
                  <TextInput
                    label="First Name"
                    placeholder={input.firstName}
                    setInput={setInput}
                    input=""
                    name="firstName"
                    disable={true}
                    type="text"
                    error={error.firstName}
                    setError={(value) =>
                      setError({ ...error, firstName: value })
                    }
                    onChangeFunction={handleInputChange}
                    width={nameInputWidth}
                    errorMessage="Enter valid name"
                    keyUp={keyUp}
                    setKeyUp={setKeyUp}
                  />
                </div>
                <div className="input-container">
                  <TextInput
                    label="Last Name"
                    placeholder={input.lastName}
                    setInput={setInput}
                    disable={true}
                    input=""
                    name="lastName"
                    type="text"
                    error={error.lastName}
                    setError={(value) =>
                      setError({ ...error, lastName: value })
                    }
                    onChangeFunction={handleInputChange}
                    width={nameInputWidth}
                    errorMessage="Enter valid name"
                    keyUp={keyUp}
                    setKeyUp={setKeyUp}
                  />
                </div>
              </div>
            </div>
            <div className="name-form-container">
              <div className="input-label">Email</div>
              <div className="input-container">
                <TextInput
                  placeholder={input.email}
                  setInput={setInput}
                  input=""
                  name="email"
                  type="text"
                  width={emailInputWidth}
                  disable={true}
                  errorMessage="Enter valid email"
                  keyUp={keyUp}
                  setKeyUp={setKeyUp}
                />
              </div>
            </div>
            {input.phoneNumber !== "" && (
              <div className="name-form-container">
                <div className="input-label">Phone Number</div>
                <div className="input-container">
                  <CustomPhoneInput
                    disabled={true}
                    value={input.phoneNumber || ""}
                    onChange={(phoneNumber) =>
                      setInput({ ...input, phoneNumber })
                    } // Passes only the phone number string
                    width={fullInputWidth}
                    error={error.phoneNumber}
                    errorMessage="Enter valid phone number"
                  />
                </div>
              </div>
            )}
            <div className="privilege-name-form-container">
              <div className="input-label">Access Privilege</div>
              <div className="input-container">
                <Dropdown
                  disabled={role === "EDITOR" || role === "VIEWER"}
                  options={accessPrivilegeOptions}
                  name="role"
                  placeholder="Choose an option..."
                  error={false}
                  onChangeFunction={handleInputChange}
                  input={input}
                  width={fullInputWidth}
                />
              </div>
            </div>
            {input.role !== "ADMIN" && input.role !== "SUPERADMIN" && (
              <div className="name-form-container">
                <div className="input-label">Selected Modules</div>
                <div className="module-form-container">
                  <div className="modules-grid">
                    {privilegesWithCheckboxes.map((module, index) => (
                      <div className="module-item" key={index}>
                        <div className="module-title">{module.name}</div>
                        <div className="checkbox-group">
                          {module.checkboxes.map((checkbox, checkboxIndex) => (
                            <Checkbox
                              disabled={role === "VIEWER" || role === "EDITOR"}
                              key={checkboxIndex}
                              value={checkbox.key}
                              text={checkbox.label}
                              checked={!!input.privileges[checkbox.key]}
                              onChange={() =>
                                handleCheckboxChange(checkbox.key)
                              }
                            />
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                  {error.privileges && (
                    <div
                      className={`privileges-error ${
                        error.privileges ? "visible" : ""
                      }`}
                    >
                      Select at least one module
                    </div>
                  )}
                </div>
              </div>
            )}

            <div className="button-container-edit">
              <SecondaryButton
                width="6rem"
                label="Cancel"
                imageIcon={false}
                onClickFunction={handleReset}
              />
              <PrimaryButton
                width="6rem"
                disable={isSubmitDisabled}
                label="Save"
                onClickFunction={handleEdit}
                loading={loading}
              />
            </div>
          </>
        );
      case "view":
        return (
          <>
            <div className="name-form-container">
              <div className="input-label">Name</div>
              <div className="input-containers">
                <div className="input-container">
                  <TextInput
                    disable={true}
                    label="First Name"
                    placeholder={input.firstName}
                    name="firstName"
                    type="text"
                    width={nameInputWidth}
                  />
                </div>
                <div className="input-container">
                  <TextInput
                    disable={true}
                    label="Last Name"
                    placeholder={input.lastName}
                    name="lastName"
                    type="text"
                    width={nameInputWidth}
                  />
                </div>
              </div>
            </div>
            <div className="name-form-container">
              <div className="input-label">Email</div>
              <div className="input-container">
                <TextInput
                  placeholder={input.email}
                  setInput={setInput}
                  input=""
                  name="email"
                  type="text"
                  width={emailInputWidth}
                  disable={true}
                />
              </div>
            </div>
            {input.phoneNumber !== "" && (
              <div className="name-form-container">
                <div className="input-label">Phone Number</div>
                <div className="input-container">
                  <CustomPhoneInput
                    disabled={true}
                    value={input.phoneNumber || ""}
                    onChange={(phoneNumber) =>
                      setInput({ ...input, phoneNumber })
                    } // Passes only the phone number string
                    width={fullInputWidth}
                    error={error.phoneNumber}
                    errorMessage="Enter valid phone number"
                  />
                </div>
              </div>
            )}
            <div className="privilege-name-form-container">
              <div className="input-label">Access Privilege</div>
              <div className="input-container">
                <Dropdown
                  disabled={true}
                  options={accessPrivilegeOptions}
                  name="role"
                  placeholder="Choose an option..."
                  input={input}
                  width={fullInputWidth}
                />
              </div>
            </div>
            {input.role !== "ADMIN" && input.role !== "SUPERADMIN" && (
              <div className="name-form-container">
                <div className="input-label">Selected Modules</div>
                <div className="module-form-container">
                  <div className="modules-grid">
                    {privilegesWithCheckboxes.map((module, index) => (
                      <div className="module-item" key={index}>
                        <div className="module-title">{module.name}</div>
                        <div className="checkbox-group">
                          {module.checkboxes.map((checkbox, checkboxIndex) => (
                            <Checkbox
                              disabled={true}
                              key={checkboxIndex}
                              value={checkbox.key}
                              text={checkbox.label}
                              checked={!!input.privileges[checkbox.key]}
                              onChange={() =>
                                handleCheckboxChange(checkbox.key)
                              }
                            />
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
            <div className="button-container-edit">
              <SecondaryButton
                width="8rem"
                label="Cancel"
                imageIcon={false}
                onClickFunction={handleReset}
              />
            </div>
          </>
        );
      default:
        return (
          <>
            <Table
              data={fetchedAdministratorData?.admins || []}
              searchText="Search by full name..."
              headers={headers}
              showEmptyHeader={true}
              showFilter={false}
              showSearchBar={true}
              handlePopoverAction={handlePopoverAction}
              onClick={handleOpenModalInvite}
              module="admin"
              loading={loading}
              showTableAction={role === "ADMIN" || role === "SUPERADMIN"}
              height="75vh"
              totalRecords={fetchedAdministratorData?.pagination?.total || 0}
              useBackendPagination={true}
              onPageChange={fetchAdministrators}
              totalPages={fetchedAdministratorData?.pagination?.totalPages || 1}
              currentPage={pageNumber}
              searchTerm={searchTerm}
              setSearchTerm={setSearchTerm}
            />
            <InviteUsersModal
              error={error}
              setError={setError}
              onChangeFunction={handleInputChange}
              isOpen={showInviteModal}
              onClose={() => setShowInviteModal(false)}
              loading={loading}
              setShowSnackbar={setShowSnackbar}
              setSnackbarSubtitle={setSnackbarSubtitle}
              setSnackbarTitle={setSnackbarTitle}
              setSnackbarType={setSnackbarType}
              setLoading={setLoading}
              handleReset={handleReset}
              fetchAdministrators={fetchAdministrators}
              institutionIdOverride={institutionId}
            />
          </>
        );
    }
  };

  return (
    <div
      className={`main-page admin-user-page ${
        masterAdminMode ? "master-admin-embedded-page" : ""
      } ${!action ? "admin-user-table-view" : ""}`}
    >
      {!masterAdminMode && (
        <NavBar type="adminUser" action={action} handleReset={handleReset} />
      )}
      <div
        className={`content-area admin-user-content-area ${
          masterAdminMode ? "master-admin-embedded-content" : ""
        }`}
      >
        {!masterAdminMode && (
          <div className="title-container mobile-hide-page-title">
            {renderTitle()}
          </div>
        )}
        <div className="main-content-container admin-user-main-content-container">
          {renderContent()}
        </div>
      </div>
      <DeleteModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onDelete={handleDelete}
        itemName={selectedRowData}
        module="admin"
        loading={loading}
      />
      {showSnackbar && (
        <div className="notification">
          <SnackBar
            state={showSnackbar}
            setState={setShowSnackbar}
            title={snackbarTitle}
            subtitle={snackbarSubtitle}
            module={snackbarType}
            action={snackbarType}
          />
        </div>
      )}
    </div>
  );
}
